import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'

interface Account {
  id: string
  branch_id: string
  opening_balance: string
  current_balance: string
  variance_ack_threshold: string
}

interface EntryRow {
  id: string
  entry_type: 'Withdrawal' | 'Expense'
  expense_head_id: string | null
  expense_head_name: string | null
  trip_id: string | null
  trip_no: string | null
  origin: string | null
  destination: string | null
  amount: string
  entry_date: string
  remarks: string | null
  is_override: boolean
  balance_after: string
}

interface Closing {
  id: string
  closing_date: string
  system_closing_balance: string
  physically_counted_balance: string
  variance: string
  remarks: string | null
  verified_at: string | null
  acknowledged_at: string | null
  reopened_at: string | null
  reopen_reason: string | null
}

/**
 * Stage 6 §4/§5: per-branch petty cash register, quick entry, and daily
 * closing. Branch-scoped like the header's own branch selector — an
 * all-branches user (typically Owner) needs a picker here too, since a
 * petty cash account is inherently one-per-branch.
 */
export function PettyCashPage() {
  const { branches, currentBranchId } = useAuth()
  const [branchId, setBranchId] = useState<string | null>(currentBranchId ?? branches[0]?.id ?? null)
  const [account, setAccount] = useState<Account | null>(null)
  const [entries, setEntries] = useState<EntryRow[]>([])
  const [closing, setClosing] = useState<Closing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [entryFormOpen, setEntryFormOpen] = useState(false)
  const [closingFormOpen, setClosingFormOpen] = useState(false)

  const reload = useCallback(async () => {
    if (!branchId) return
    setError(null)
    try {
      const [acct, register, closingRes] = await Promise.all([
        api.get<Account>(`/branches/${branchId}/petty-cash`),
        api.get<{ rows: EntryRow[] }>(`/branches/${branchId}/petty-cash/entries?pageSize=50`),
        api.get<Closing | null>(`/branches/${branchId}/petty-cash/closing`),
      ])
      setAccount(acct)
      setEntries(register.rows)
      setClosing(closingRes)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load petty cash data')
    }
  }, [branchId])

  useEffect(() => {
    void reload()
  }, [reload])

  const overThreshold =
    account && closing && !closing.verified_at && Math.abs(Number(closing.variance)) > Number(account.variance_ack_threshold)

  async function acknowledge() {
    if (!closing) return
    try {
      await api.post(`/petty-cash-closing/${closing.id}/acknowledge`)
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to acknowledge')
    }
  }

  async function reopen() {
    if (!closing) return
    const reason = window.prompt("Reason for reopening today's closing?")
    if (!reason) return
    try {
      await api.post(`/petty-cash-closing/${closing.id}/reopen`, { reason })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to reopen')
    }
  }

  return (
    <div>
      <div className="tf-toolbar">
        <h2 style={{ margin: 0 }}>Petty Cash</h2>
        {branches.length > 0 && (
          <select className="tf-branch-select" value={branchId ?? ''} onChange={(e) => setBranchId(e.target.value || null)}>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <p className="tf-error">{error}</p>}

      {account && (
        <div className="tf-card">
          <p className="tf-note">Current balance</p>
          <h1 style={{ margin: 0 }}>₹{account.current_balance}</h1>
          <p className="tf-note">
            Opening ₹{account.opening_balance} · Variance acknowledgment threshold ₹{account.variance_ack_threshold}
          </p>
        </div>
      )}

      {closing && (
        <div className="tf-card" style={{ marginTop: '1rem' }}>
          <p className="tf-note">
            Today's closing ({new Date(closing.closing_date).toLocaleDateString()}): system ₹{closing.system_closing_balance}, counted ₹
            {closing.physically_counted_balance}, variance ₹{closing.variance}
            {closing.verified_at ? ' — Verified' : overThreshold ? ' — awaiting Owner acknowledgment' : ' — not yet verified'}
          </p>
          <div className="tf-row-actions">
            {overThreshold && (
              <button type="button" className="tf-button-primary" onClick={() => void acknowledge()}>
                Acknowledge (Owner)
              </button>
            )}
            {closing.verified_at && (
              <button type="button" onClick={() => void reopen()}>
                Reopen
              </button>
            )}
          </div>
        </div>
      )}

      <div className="tf-form-actions" style={{ margin: '1rem 0' }}>
        <button type="button" className="tf-button-primary" onClick={() => setEntryFormOpen(true)}>
          + New Entry
        </button>
        <button type="button" onClick={() => setClosingFormOpen(true)}>
          Daily Closing
        </button>
      </div>

      <table className="tf-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Head</th>
            <th>Trip</th>
            <th>Amount</th>
            <th>Balance After</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr>
              <td colSpan={6}>No entries found</td>
            </tr>
          )}
          {entries.map((e) => (
            <tr key={e.id}>
              <td>{new Date(e.entry_date).toLocaleDateString()}</td>
              <td>{e.entry_type}</td>
              <td>{e.expense_head_name ?? '—'}</td>
              <td>{e.trip_no ? `${e.trip_no} — ${e.origin} → ${e.destination}` : '—'}</td>
              <td>
                {e.entry_type === 'Withdrawal' ? '+' : '−'}₹{e.amount}
                {e.is_override && ' (override)'}
              </td>
              <td>₹{e.balance_after}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {entryFormOpen && branchId && (
        <EntryFormModal
          branchId={branchId}
          onClose={() => setEntryFormOpen(false)}
          onSaved={async () => {
            setEntryFormOpen(false)
            await reload()
          }}
        />
      )}

      {closingFormOpen && branchId && account && (
        <ClosingFormModal
          branchId={branchId}
          currentBalance={account.current_balance}
          onClose={() => setClosingFormOpen(false)}
          onSaved={async () => {
            setClosingFormOpen(false)
            await reload()
          }}
        />
      )}
    </div>
  )
}

interface ExpenseHead {
  id: string
  name: string
  category: 'Trip' | 'Office'
}

interface TripOption {
  id: string
  trip_no: string
  origin: string
  destination: string
}

function EntryFormModal({
  branchId,
  onClose,
  onSaved,
}: {
  branchId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [entryType, setEntryType] = useState<'Withdrawal' | 'Expense'>('Expense')
  const [expenseHeads, setExpenseHeads] = useState<ExpenseHead[]>([])
  const [expenseHeadId, setExpenseHeadId] = useState('')
  const [amount, setAmount] = useState('')
  const [entryDate, setEntryDate] = useState('')
  const [remarks, setRemarks] = useState('')
  const [tripQuery, setTripQuery] = useState('')
  const [tripOptions, setTripOptions] = useState<TripOption[]>([])
  const [tripId, setTripId] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [needsOverride, setNeedsOverride] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void api.get<{ rows: ExpenseHead[] }>('/expense-heads?pageSize=100').then((res) => setExpenseHeads(res.rows))
  }, [])

  const selectedHead = expenseHeads.find((h) => h.id === expenseHeadId)
  const showTripPicker = entryType === 'Expense' && selectedHead?.category === 'Trip'

  useEffect(() => {
    if (!showTripPicker) return
    const handle = setTimeout(() => {
      void api.get<TripOption[]>(`/trips/search?q=${encodeURIComponent(tripQuery)}`).then(setTripOptions)
    }, 300)
    return () => clearTimeout(handle)
  }, [tripQuery, showTripPicker])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        entry_type: entryType,
        amount,
        entry_date: entryDate || undefined,
        remarks: remarks || undefined,
        expense_head_id: entryType === 'Expense' ? expenseHeadId : undefined,
        trip_id: showTripPicker && tripId ? tripId : undefined,
        override_reason: needsOverride ? overrideReason || undefined : undefined,
      }
      const query = needsOverride ? '?confirm=true' : ''
      await api.post(`/branches/${branchId}/petty-cash/entries${query}`, body)
      onSaved()
    } catch (err) {
      if (err instanceof ApiError && err.requiresOverride) {
        setNeedsOverride(true)
        setError(err.message)
      } else {
        setError(err instanceof ApiError ? err.message : 'Save failed')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>New Petty Cash Entry</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Type</span>
            <select value={entryType} onChange={(e) => setEntryType(e.target.value as typeof entryType)}>
              <option value="Expense">Expense</option>
              <option value="Withdrawal">Withdrawal</option>
            </select>
          </label>

          {entryType === 'Expense' && (
            <label className="tf-field">
              <span>Expense head</span>
              <select value={expenseHeadId} onChange={(e) => setExpenseHeadId(e.target.value)} required>
                <option value="" disabled>
                  Select…
                </option>
                {expenseHeads.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {showTripPicker && (
            <label className="tf-field">
              <span>Trip (optional)</span>
              <input
                value={tripQuery}
                onChange={(e) => {
                  setTripQuery(e.target.value)
                  setTripId('')
                }}
                placeholder="Search by Trip ID or destination…"
              />
              {tripOptions.length > 0 && !tripId && (
                <div className="tf-card" style={{ marginTop: '0.4rem' }}>
                  {tripOptions.map((t) => (
                    <div key={t.id} style={{ padding: '0.3rem 0' }}>
                      <button
                        type="button"
                        className="tf-button-link"
                        onClick={() => {
                          setTripId(t.id)
                          setTripQuery(`${t.trip_no} — ${t.origin} → ${t.destination}`)
                          setTripOptions([])
                        }}
                      >
                        {t.trip_no} — {t.origin} → {t.destination}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </label>
          )}

          <label className="tf-field">
            <span>Amount (₹)</span>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          </label>

          <label className="tf-field">
            <span>Date</span>
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
          </label>

          <label className="tf-field">
            <span>Remarks</span>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </label>

          {needsOverride && (
            <label className="tf-field">
              <span>Override reason (required — this would take the float negative)</span>
              <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} required />
            </label>
          )}

          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {needsOverride ? 'Override & Save' : saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ClosingFormModal({
  branchId,
  currentBalance,
  onClose,
  onSaved,
}: {
  branchId: string
  currentBalance: string
  onClose: () => void
  onSaved: () => void
}) {
  const [counted, setCounted] = useState(currentBalance)
  const [remarks, setRemarks] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const variance = Number(counted || '0') - Number(currentBalance)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post(`/branches/${branchId}/petty-cash/closing`, {
        physically_counted_balance: counted,
        remarks: remarks || undefined,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Closing failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Daily Closing</h2>
        <p className="tf-note">System-computed balance: ₹{currentBalance}</p>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Physically counted balance (₹)</span>
            <input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} required />
            <small>Variance: ₹{variance.toFixed(2)}</small>
          </label>
          {variance !== 0 && (
            <label className="tf-field">
              <span>Remarks (required — variance is non-zero)</span>
              <input value={remarks} onChange={(e) => setRemarks(e.target.value)} required />
            </label>
          )}
          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Close Day'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
